/** The finest lattice the 3D viewer will resample a grid's surface onto. */
export const MAX_LATTICE_RESOLUTION = 300;

/**
 * The coarsest lattice. The viewer places cells by dividing by `resolution - 1`, so a
 * lattice needs at least 2 cells across.
 */
const MIN_LATTICE_RESOLUTION = 2;

/**
 * Feet per degree of latitude. `StructureGrid#extract_grid_cell_size_sql` uses this to turn
 * a grid's pixel size into cellWidthFeet / cellHeightFeet; dividing by it turns them back.
 */
export const DEGREE_TO_FEET = 364173;

const cellsAcross = (span: number, pixelSize: number | null): number => {
  if (pixelSize === null) return MAX_LATTICE_RESOLUTION;

  return Math.max(MIN_LATTICE_RESOLUTION, Math.floor(span / pixelSize));
};

/**
 * How many cells across the lattice the 3D viewer resamples a grid's surface onto.
 *
 * A lattice finer than the grid's own pixels leaves whole rows and columns with no point
 * in them. A triangle is only drawn where all three of its corners have data, so the mesh
 * tears along each empty row and column and the surface renders as stripes. The lattice
 * therefore never gets finer than the grid it is drawing: MAX_LATTICE_RESOLUTION for a
 * grid with pixels to spare, and the grid's own pixel count for a coarse one.
 *
 * Falls back to MAX_LATTICE_RESOLUTION when the cell size is unknown, which is what the
 * viewer used for every grid before.
 *
 * @return {number} Cells across one side of the lattice.
 */
export const latticeResolution = ({
  minLon,
  maxLon,
  minLat,
  maxLat,
  cellWidthFeet,
  cellHeightFeet,
}: {
  minLon: number;
  maxLon: number;
  minLat: number;
  maxLat: number;
  cellWidthFeet: number | null;
  cellHeightFeet: number | null;
}): number => {
  const midLatRadians = (((minLat + maxLat) / 2) * Math.PI) / 180;
  const pixelWidth = cellWidthFeet
    ? cellWidthFeet / (DEGREE_TO_FEET * Math.cos(midLatRadians))
    : null;
  const pixelHeight = cellHeightFeet ? cellHeightFeet / DEGREE_TO_FEET : null;

  return Math.min(
    MAX_LATTICE_RESOLUTION,
    cellsAcross(maxLon - minLon, pixelWidth),
    cellsAcross(maxLat - minLat, pixelHeight)
  );
};

/** How many of its 8 neighbours an empty lattice cell needs with data to be filled. */
const MIN_NEIGHBORS_TO_FILL = 5;

/** An empty lattice cell and the elevation to fill it with. */
interface LatticeFill {
  x: number;
  y: number;
  elevation: number;
}

/**
 * The empty cells inside a surface that can be filled, each with the average elevation of
 * its neighbours with data.
 *
 * The 3D viewer fills these so the mesh does not tear around a cell that happened to
 * receive no point. A cell needs MIN_NEIGHBORS_TO_FILL of its 8 neighbours to have data.
 * That fills a gap one cell wide but not one two cells wide, and it leaves the surface's
 * outline alone rather than growing it a cell outwards all the way round.
 *
 * Only cells that already have data count as neighbours, not cells this returns, so a
 * filled cell cannot help fill the next one and walk the fill across the lattice.
 *
 * @param {Array<Array<?number>>} elevationGrid Each lattice cell's elevation by row, or
 *   null for a cell with no data.
 * @return {LatticeFill[]} The cells to fill.
 */
export const fillableHoles = (
  elevationGrid: (number | null)[][]
): LatticeFill[] => {
  const fills: LatticeFill[] = [];

  elevationGrid.forEach((row, y) => {
    row.forEach((cell, x) => {
      if (cell !== null) return;

      let sum = 0;
      let count = 0;
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const neighbor = elevationGrid[y + dy]?.[x + dx];
          if (neighbor === null || neighbor === undefined) continue;

          sum += neighbor;
          count++;
        }
      }

      if (count >= MIN_NEIGHBORS_TO_FILL) {
        fills.push({ x, y, elevation: sum / count });
      }
    });
  });

  return fills;
};

// Convert longitude/latitude to tile coordinates
export const lonLatToTile = (lon: number, lat: number, zoom: number) => {
  const n = Math.pow(2, zoom);
  const x = Math.floor(((lon + 180) / 360) * n);
  const y = Math.floor(
    ((1 -
      Math.log(
        Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)
      ) /
        Math.PI) /
      2) *
      n
  );
  return { x, y };
};
